import type { Importer } from '@feathr/blackbox';

import type { IImportsFilters, TImportType } from './ImportsFilters';

/** The backend stores `date_created` with microseconds and no timezone. */
const START_OF_DAY = 'T00:00:00.000000';
const END_OF_DAY = 'T23:59:59.999999';

function dateOnly(value: string): string {
  return value.split('T')[0];
}

/** Turns the drawer's date operator and date(s) into `date_created` range filters. */
export function getDateFilters(filters: IImportsFilters): Record<string, string> {
  const operator = filters.date_operator || 'is';
  const dateFilters: Record<string, string> = {};

  switch (operator) {
    case 'is':
      if (filters.date_created) {
        const day = dateOnly(filters.date_created);
        dateFilters.date_created__gte = `${day}${START_OF_DAY}`;
        dateFilters.date_created__lte = `${day}${END_OF_DAY}`;
      }
      break;

    case 'is_before':
      if (filters.date_created) {
        dateFilters.date_created__lt = `${dateOnly(filters.date_created)}${START_OF_DAY}`;
      }
      break;

    case 'is_after':
      if (filters.date_created) {
        dateFilters.date_created__gt = `${dateOnly(filters.date_created)}${END_OF_DAY}`;
      }
      break;

    case 'is_between':
      if (filters.date_created__gte) {
        dateFilters.date_created__gte = `${dateOnly(filters.date_created__gte)}${START_OF_DAY}`;
      }
      if (filters.date_created__lte) {
        dateFilters.date_created__lte = `${dateOnly(filters.date_created__lte)}${END_OF_DAY}`;
      }
      break;
  }

  return dateFilters;
}

/** Which of the table's import types a row is. */
export function getImportType(importer: Importer): TImportType {
  if (importer.get('kind') === 'partner') {
    return 'partner';
  }
  return importer.isOptOut ? 'opt-out' : 'person';
}

function getTypeFilters(importType: TImportType | undefined): Record<string, unknown> {
  switch (importType) {
    // An opt-out import is a person import with overrides; a person import has none.
    case 'person':
      return { kind: 'person', overrides__size: 0 };

    case 'opt-out':
      return { kind: 'person', overrides__not__size: 0 };

    case 'partner':
      return { kind: 'partner' };

    default:
      return {};
  }
}

/**
 * Builds the importers list request's `filters` from the drawer's applied filters. Without
 * `includeAllKinds` the list holds person imports only.
 */
export function getImportsListFilters(
  filters: IImportsFilters,
  includeAllKinds = false,
): Record<string, unknown> {
  return {
    ...(includeAllKinds ? {} : { kind: 'person' }),
    ...(filters.state__in?.length ? { state__in: filters.state__in } : {}),
    ...getDateFilters(filters),
    ...getTypeFilters(filters.importType),
  };
}
